import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { Plus, Edit, Trash2, X, Layers, Package, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Product Picker ─────────────────────────────────────────────────
function ProductPicker({ selected, onChange }) {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/products`, { params: { limit: 100 }, withCredentials: true })
      .then(r => setProducts(r.data.products.filter(p => p.published)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = products.filter(p => p.title.toLowerCase().includes(search.toLowerCase()));
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id]);

  return (
    <div>
      <label className="text-ink/55 text-xs uppercase tracking-widest mb-1.5 block">
        Products ({selected.length} selected)
      </label>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products..."
        className="w-full bg-ink/[0.04] border border-ink/10 rounded-xl px-4 py-2 text-ink text-sm mb-2 focus:outline-none focus:border-honey" />
      <div className="max-h-52 overflow-y-auto space-y-1 bg-ink/[0.03] rounded-xl p-2">
        {loading ? <p className="text-ink/45 text-sm text-center py-4">Loading...</p> :
          filtered.length === 0 ? <p className="text-ink/45 text-sm text-center py-4">No published products</p> :
          filtered.map(p => (
            <button key={p.id} type="button" onClick={() => toggle(p.id)}
              data-testid={`picker-${p.id}`}
              className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors text-left ${selected.includes(p.id) ? 'bg-honey/10 border border-honey/20' : 'hover:bg-ink/[0.04]'}`}>
              <div className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 ${selected.includes(p.id) ? 'bg-honey border-honey' : 'border-ink/15'}`}>
                {selected.includes(p.id) && <Check className="w-3 h-3 text-black" />}
              </div>
              <div className="w-8 h-8 rounded-lg overflow-hidden bg-ink/[0.04] flex-shrink-0">
                {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-ink text-xs font-medium truncate">{p.title}</p>
                <p className="text-honey-700 text-xs">${p.price?.toFixed(2)}</p>
              </div>
            </button>
          ))
        }
      </div>
    </div>
  );
}

// ── Collection Modal ───────────────────────────────────────────────
function CollectionModal({ collection, onClose, onSave }) {
  const [form, setForm] = useState(collection || {
    title: '', description: '', image: '', product_ids: [], active: true
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto py-8 px-4">
      <div className="bg-white border border-ink/10 rounded-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-ink/10">
          <h2 className="text-ink font-bold">{collection ? 'Edit Collection' : 'Create Collection'}</h2>
          <button onClick={onClose} className="text-ink/55 hover:text-ink"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSave} className="p-6 space-y-4">
          <div>
            <label className="text-ink/55 text-xs uppercase tracking-widest mb-1.5 block">Title *</label>
            <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} required
              data-testid="collection-title-input"
              className="w-full bg-ink/[0.04] border border-ink/10 rounded-xl px-4 py-2.5 text-ink text-sm focus:outline-none focus:border-honey" />
          </div>
          <div>
            <label className="text-ink/55 text-xs uppercase tracking-widest mb-1.5 block">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2}
              className="w-full bg-ink/[0.04] border border-ink/10 rounded-xl px-4 py-2.5 text-ink text-sm focus:outline-none focus:border-honey resize-none" />
          </div>
          <div>
            <label className="text-ink/55 text-xs uppercase tracking-widest mb-1.5 block">Cover Image URL</label>
            <input value={form.image} onChange={e => setForm({...form, image: e.target.value})}
              placeholder="https://..."
              className="w-full bg-ink/[0.04] border border-ink/10 rounded-xl px-4 py-2.5 text-ink text-sm focus:outline-none focus:border-honey" />
            {form.image && <img src={form.image} alt="" className="mt-2 h-24 rounded-xl object-cover" />}
          </div>
          <ProductPicker selected={form.product_ids} onChange={ids => setForm({...form, product_ids: ids})} />
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})} className="w-4 h-4 accent-honey" />
            <span className="text-ink/75 text-sm">Active (visible on storefront)</span>
          </label>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving}
              data-testid="save-collection-btn"
              className="flex-1 bg-honey text-black font-bold py-2.5 rounded-xl hover:bg-honey-600 transition-colors disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Collection'}
            </button>
            <button type="button" onClick={onClose} className="px-6 bg-ink/[0.04] text-ink/75 rounded-xl">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────
export default function AdminCollections() {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCollection, setEditCollection] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => { fetchCollections(); }, []);

  const fetchCollections = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/collections`, { withCredentials: true });
      setCollections(r.data.collections);
    } finally { setLoading(false); }
  };

  const handleSave = async (form) => {
    if (editCollection) {
      await axios.put(`${API}/admin/collections/${editCollection.id}`, form, { withCredentials: true });
    } else {
      await axios.post(`${API}/admin/collections`, form, { withCredentials: true });
    }
    setShowAdd(false); setEditCollection(null);
    fetchCollections();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this collection?')) return;
    await axios.delete(`${API}/admin/collections/${id}`, { withCredentials: true });
    fetchCollections();
  };

  return (
    <AdminLayout>
      {(showAdd || editCollection) && (
        <CollectionModal
          collection={editCollection}
          onClose={() => { setShowAdd(false); setEditCollection(null); }}
          onSave={handleSave}
        />
      )}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-ink" style={{ fontFamily: 'Outfit, sans-serif' }}>Collections</h1>
          <p className="text-ink/55 text-sm">Curated product groupings for the storefront</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-collection-btn"
          className="flex items-center gap-2 px-4 py-2 bg-honey text-black rounded-xl font-bold text-sm hover:bg-honey-600 transition-colors">
          <Plus className="w-4 h-4" /> New Collection
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center pt-20">
          <div className="w-8 h-8 border-2 border-honey border-t-transparent rounded-full animate-spin" />
        </div>
      ) : collections.length === 0 ? (
        <div className="text-center py-24">
          <Layers className="w-12 h-12 text-ink/30 mx-auto mb-4" />
          <p className="text-ink/55 text-lg">No collections yet</p>
          <p className="text-ink/30 text-sm mt-2">Create curated groups to showcase on your storefront</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {collections.map(col => (
            <div key={col.id} data-testid={`collection-card-admin-${col.id}`}
              className="admin-card rounded-xl overflow-hidden group">
              <div className="relative h-36 bg-ink/[0.04]">
                {col.image
                  ? <img src={col.image} alt={col.title} className="w-full h-full object-cover opacity-70" />
                  : <div className="w-full h-full flex items-center justify-center"><Layers className="w-8 h-8 text-ink/15" /></div>}
                <div className="absolute inset-0 bg-gradient-to-t from-white/80 to-transparent" />
                <div className={`absolute top-3 right-3 text-xs font-bold px-2 py-1 rounded-full ${col.active ? 'bg-green-500/10 text-green-600' : 'bg-ink/[0.04] text-ink/45'}`}>
                  {col.active ? 'Live' : 'Hidden'}
                </div>
              </div>
              <div className="p-5">
                <h3 className="text-ink font-bold mb-1">{col.title}</h3>
                {col.description && <p className="text-ink/55 text-sm line-clamp-1">{col.description}</p>}
                <p className="text-honey-700 text-xs mt-2">{col.product_count} products</p>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => setEditCollection(col)}
                    className="flex-1 flex items-center justify-center gap-2 py-2 bg-ink/[0.04] hover:bg-ink/[0.07] text-ink/75 hover:text-ink rounded-xl text-sm transition-colors">
                    <Edit className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={() => handleDelete(col.id)}
                    className="px-4 py-2 bg-red-500/5 hover:bg-red-500/10 text-red-600/50 hover:text-red-600 rounded-xl text-sm transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
